import { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Flex, Text, VStack, HStack, Badge, Spinner, Button,
    useToast, Avatar, IconButton, Tooltip, Input, InputGroup, InputLeftElement,
    Progress, Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody,
    DrawerCloseButton, useBreakpointValue,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Table, Thead, Tbody, Tr, Th, Td,
    FormControl, FormLabel, Textarea, SimpleGrid, InputLeftAddon
} from "@chakra-ui/react";
import {
    Search, RefreshCw, Sparkles, Copy, ArrowLeft, MessageSquare,
    ChevronDown, History, Clock, Info, ChevronRight, AlertTriangle,
    FileText, Trash2, Plus, Package
} from "lucide-react";
import {
    getPoliContatosVendas, getPoliMensagensContato,
    postPoliAssistente, getPoliAssistente, getPoliHistoricoSugestoes,
    gerarOrcamento, getProdutos, extrairProdutosSugestao
} from "../services/api";

const ALTURA_TOTAL = "calc(100vh - 130px)";

// Mapeamento de erros retornados pela API (campo `tipo`)
const TIPOS_ERRO = {
    rate_limit: {
        titulo: "Limite de requisicoes atingido",
        descricao: "A API Claude bloqueou temporariamente. Aguarde cerca de 1 minuto e tente novamente.",
        status: "warning",
        podeRetentar: true
    },
    overloaded: {
        titulo: "API sobrecarregada",
        descricao: "A Anthropic esta com instabilidade. Tente novamente em alguns segundos.",
        status: "warning",
        podeRetentar: true
    },
    timeout: {
        titulo: "Tempo esgotado",
        descricao: "A IA demorou demais para responder. Tente novamente.",
        status: "warning",
        podeRetentar: true
    },
    auth: {
        titulo: "Credenciais invalidas",
        descricao: "A chave da API Anthropic expirou ou esta incorreta. Contate o administrador.",
        status: "error",
        podeRetentar: false
    },
    empty: {
        titulo: "Historico vazio",
        descricao: "Nao foi possivel montar o historico da conversa apos o corte de sessao. Pode ser uma conversa muito curta.",
        status: "warning",
        podeRetentar: false
    },
    empty_response: {
        titulo: "Sem resposta",
        descricao: "A IA nao gerou texto para esta conversa. Verifique se ha contexto suficiente.",
        status: "warning",
        podeRetentar: true
    },
    max_iterations: {
        titulo: "Consulta muito complexa",
        descricao: "O assistente fez muitas buscas sem concluir. Tente simplificar ou regenerar.",
        status: "error",
        podeRetentar: true
    },
    exception: {
        titulo: "Erro interno",
        descricao: "Ocorreu um erro inesperado no servidor ao gerar a sugestao.",
        status: "error",
        podeRetentar: true
    },
    unexpected: {
        titulo: "Resposta inesperada",
        descricao: "A IA retornou uma resposta em formato nao reconhecido.",
        status: "error",
        podeRetentar: true
    }
};

const mapearErro = (res) => {
    const tipo = res?.tipo;
    const cfg = TIPOS_ERRO[tipo];
    if (cfg) return { ...cfg, tipo };
    return {
        tipo: tipo || "unknown",
        titulo: "Erro ao gerar sugestao",
        descricao: res?.erro || "Erro desconhecido ao chamar o assistente.",
        status: "error",
        podeRetentar: true
    };
};

const AssistenteVendas = () => {
    const toast = useToast();
    const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;

    // Contatos
    const [contatos, setContatos] = useState([]);
    const [loadingContatos, setLoadingContatos] = useState(true);
    const [totalContatos, setTotalContatos] = useState(0);
    const [filtroContato, setFiltroContato] = useState("");
    const [filtroStatus, setFiltroStatus] = useState(null);
    const [filtroDias, setFiltroDias] = useState(2);

    // Conversa
    const [contatoSelecionado, setContatoSelecionado] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [loadingMensagens, setLoadingMensagens] = useState(false);

    // Sugestao atual (a que esta sendo exibida - pode ser historica)
    const [sugestao, setSugestao] = useState(null);
    const [sugestaoAtualId, setSugestaoAtualId] = useState(null);
    const [loadingSugestao, setLoadingSugestao] = useState(false);
    const [tempoSugestao, setTempoSugestao] = useState(0);
    const [erroSugestao, setErroSugestao] = useState(null);

    // Historico
    const [historico, setHistorico] = useState([]);
    const [loadingHistorico, setLoadingHistorico] = useState(false);
    const [tabSugestao, setTabSugestao] = useState(0); // 0 = Atual, 1 = Historico

    // Mobile bottom sheet
    const [sheetAberto, setSheetAberto] = useState(false);

    // Orcamento
    const [modalOrcamento, setModalOrcamento] = useState(false);
    const [produtosOrcamento, setProdutosOrcamento] = useState([]);
    const [loadingProdutos, setLoadingProdutos] = useState(false);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [nomeCliente, setNomeCliente] = useState('');
    const [telefoneCliente, setTelefoneCliente] = useState('');
    const [emailCliente, setEmailCliente] = useState('');
    const [documentoCliente, setDocumentoCliente] = useState('');
    const [enderecoCliente, setEnderecoCliente] = useState('');
    const [valorFrete, setValorFrete] = useState('');
    const [observacoesOrc, setObservacoesOrc] = useState('');
    const [descontoOrc, setDescontoOrc] = useState(0);
    const [buscaManual, setBuscaManual] = useState('');
    const [resultadosBusca, setResultadosBusca] = useState([]);
    const [loadingBusca, setLoadingBusca] = useState(false);

    const mensagensRef = useRef(null);
    const dataFetchedRef = useRef(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;
        carregarContatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Timer para contar tempo da geracao
    useEffect(() => {
        if (loadingSugestao) {
            setTempoSugestao(0);
            timerRef.current = setInterval(() => {
                setTempoSugestao(prev => prev + 1);
            }, 1000);
        } else if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [loadingSugestao]);

    // Fecha o sheet mobile ao sair de um contato
    useEffect(() => {
        if (!contatoSelecionado) setSheetAberto(false);
    }, [contatoSelecionado]);

    const carregarContatos = async (status = filtroStatus, dias = filtroDias) => {
        setLoadingContatos(true);
        const res = await getPoliContatosVendas(status, dias);
        if (res && res.success) {
            setContatos(res.data || []);
            setTotalContatos(res.total || 0);
        } else {
            toast({ title: "Erro ao carregar contatos", status: "error", duration: 3000 });
        }
        setLoadingContatos(false);
    };

    const carregarTodasMensagens = async (contactId) => {
        let todas = [];
        let page = 1;
        let totalPages = 1;
        while (page <= totalPages) {
            const res = await getPoliMensagensContato(contactId, 'created_at', page);
            if (res && res.success) {
                todas = [...todas, ...(res.data.data || [])];
                totalPages = res.data.last_page || 1;
            } else {
                break;
            }
            page++;
        }
        return todas;
    };

    const scrollParaFinal = useCallback(() => {
        setTimeout(() => {
            if (mensagensRef.current) {
                mensagensRef.current.scrollTop = mensagensRef.current.scrollHeight;
            }
        }, 100);
    }, []);

    const selecionarContato = async (contato) => {
        setContatoSelecionado(contato);
        setSugestao(null);
        setSugestaoAtualId(null);
        setErroSugestao(null);
        setHistorico([]);
        setTabSugestao(0);
        setMensagens([]);
        setLoadingMensagens(true);
        setLoadingHistorico(true);

        const [todasMsgs, histRes, sugRes] = await Promise.all([
            carregarTodasMensagens(contato.id),
            getPoliHistoricoSugestoes(contato.id, 50),
            getPoliAssistente(contato.id)
        ]);

        setMensagens(todasMsgs);
        setLoadingMensagens(false);
        scrollParaFinal();

        const historicoList = histRes?.data || [];
        setHistorico(historicoList);
        setLoadingHistorico(false);

        // Sugestao exibida: prioriza endpoint atual, cai para historico[0]
        if (sugRes && sugRes.sugestao) {
            setSugestao(sugRes);
            setSugestaoAtualId(sugRes.id);
        } else if (historicoList.length > 0) {
            setSugestao(historicoList[0]);
            setSugestaoAtualId(historicoList[0].id);
        }
    };

    const gerarSugestao = async () => {
        if (!contatoSelecionado) return;
        setLoadingSugestao(true);
        setErroSugestao(null);
        setTabSugestao(0);

        // No mobile, abre o sheet para o usuario acompanhar o loading
        if (isMobile) setSheetAberto(true);

        const res = await postPoliAssistente(contatoSelecionado.id);

        if (res && res.sugestao) {
            setSugestao(res);
            setSugestaoAtualId(res.id);
            setHistorico(prev => [res, ...prev.filter(h => h.id !== res.id)]);
            toast({
                title: "Sugestao gerada!",
                status: "success",
                duration: 3000,
                position: isMobile ? "top" : "bottom-right"
            });
        } else if (res && res.erro) {
            const info = mapearErro(res);
            setErroSugestao(info);
            toast({
                title: info.titulo,
                description: info.descricao,
                status: info.status,
                duration: 5000,
                isClosable: true,
                position: isMobile ? "top" : "bottom-right"
            });
        } else {
            const info = {
                tipo: "network",
                titulo: "Erro de conexao",
                descricao: "Nao foi possivel conectar ao servidor. Verifique sua internet.",
                status: "error",
                podeRetentar: true
            };
            setErroSugestao(info);
            toast({
                title: info.titulo,
                description: info.descricao,
                status: "error",
                duration: 5000,
                isClosable: true,
                position: isMobile ? "top" : "bottom-right"
            });
        }
        setLoadingSugestao(false);
    };

    const copiarSugestao = () => {
        if (sugestao?.sugestao) {
            navigator.clipboard.writeText(sugestao.sugestao);
            toast({
                title: "Copiado!",
                status: "info",
                duration: 2000,
                position: isMobile ? "top" : "bottom-right"
            });
        }
    };

    // ====== ORCAMENTO ======

    const formatarTelefoneOrc = (phone) => {
        if (!phone) return '';
        let num = phone.replace(/\D/g, '');
        if (num.startsWith('55') && num.length > 11) num = num.slice(2);
        num = num.replace(/^(\d{2})(\d)/g, '($1) $2');
        num = num.replace(/(\d{5})(\d)/, '$1-$2');
        num = num.replace(/(\d{4})-(\d)(\d{4})/, '$1$2-$3');
        return num.slice(0, 15);
    };

    const parseFormattedCurrency = (value) => {
        if (typeof value !== 'string' || value === '') return 0;
        const parsed = parseFloat(value.replace(/\./g, '').replace(',', '.'));
        return isNaN(parsed) ? 0 : parsed;
    };

    const formatarPreco = (valor) => {
        const n = Number(valor);
        return isNaN(n) ? '0,00' : n.toFixed(2).replace('.', ',');
    };

    const handleFreteChange = (e) => {
        const digitsOnly = e.target.value.replace(/\D/g, '');
        if (!digitsOnly) { setValorFrete(''); return; }
        setValorFrete(new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(parseInt(digitsOnly, 10) / 100));
    };

    const subTotalOrc = produtosOrcamento.reduce((t, p) => t + (p.valor_venda * (p.quantidade_orcamento || 1)), 0);
    const descontoReaisOrc = subTotalOrc * (descontoOrc / 100);
    const totalOrc = subTotalOrc - descontoReaisOrc + parseFormattedCurrency(valorFrete);

    const abrirOrcamento = async () => {
        if (!sugestao?.id) {
            toast({ title: "Gere uma sugestao primeiro", status: "warning", duration: 3000, position: isMobile ? "top" : "bottom-right" });
            return;
        }

        setEmailCliente('');
        setDocumentoCliente('');
        setEnderecoCliente('');
        setValorFrete('');
        setObservacoesOrc('');
        setDescontoOrc(0);
        setProdutosOrcamento([]);
        setResultadosBusca([]);
        setBuscaManual('');
        setNomeCliente(contatoSelecionado?.name || '');
        setTelefoneCliente(formatarTelefoneOrc(contatoSelecionado?.phone || ''));
        setModalOrcamento(true);
        setLoadingProdutos(true);

        const res = await extrairProdutosSugestao(sugestao.id);

        if (res && res.produtos && res.produtos.length > 0) {
            setProdutosOrcamento(res.produtos);
            if (res.nome_cliente) setNomeCliente(res.nome_cliente);
            if (res.telefone_cliente) setTelefoneCliente(formatarTelefoneOrc(res.telefone_cliente));
        } else if (res && res.erro) {
            toast({ title: "Nenhum produto encontrado", description: res.erro, status: "warning", duration: 5000, isClosable: true, position: isMobile ? "top" : "bottom-right" });
        } else {
            toast({ title: "Erro ao extrair produtos", description: "Nao foi possivel extrair produtos da sugestao.", status: "error", duration: 3000, position: isMobile ? "top" : "bottom-right" });
        }
        setLoadingProdutos(false);
    };

    const buscarProdutoManual = async () => {
        if (!buscaManual || buscaManual.length < 2) return;
        setLoadingBusca(true);
        try {
            const termo = encodeURIComponent(buscaManual);
            const consulta = `nome=${termo}&codGlobal=${termo}&aplicacao=&id=&idAnuncio=&codFor=&estoqueTaga=&fornecedor=&pagina=1&mktplace=&status=`;
            const result = await getProdutos(consulta);
            setResultadosBusca(result && result.data ? result.data.slice(0, 10) : []);
        } catch {
            setResultadosBusca([]);
        }
        setLoadingBusca(false);
    };

    const adicionarProduto = (produto) => {
        if (produtosOrcamento.some(p => p.produto_id === produto.produto_id)) {
            toast({ title: "Produto ja adicionado", status: "info", duration: 2000, position: isMobile ? "top" : "bottom-right" });
            return;
        }
        setProdutosOrcamento(prev => [...prev, { ...produto, quantidade_orcamento: 1 }]);
        setResultadosBusca([]);
        setBuscaManual('');
    };

    const removerProdutoOrc = (pid) => {
        setProdutosOrcamento(prev => prev.filter(p => p.produto_id !== pid));
    };

    const handleQtdChange = (pid, val) => {
        setProdutosOrcamento(prev => prev.map(p =>
            p.produto_id === pid ? { ...p, quantidade_orcamento: val === '' ? '' : Math.max(1, parseInt(val) || 1) } : p
        ));
    };

    const handleGerarPdf = async () => {
        if (produtosOrcamento.length === 0) {
            toast({ title: "Adicione pelo menos um produto", status: "warning", duration: 3000, position: isMobile ? "top" : "bottom-right" });
            return;
        }
        setLoadingPdf(true);
        try {
            const userString = window.localStorage.getItem("user");
            const user = userString ? JSON.parse(userString) : {};
            const requestData = {
                produtos: produtosOrcamento,
                nome_cliente: nomeCliente,
                documento_cliente: documentoCliente,
                endereco_cliente: enderecoCliente,
                telefone_cliente: telefoneCliente,
                email_cliente: emailCliente,
                valor_frete: parseFormattedCurrency(valorFrete),
                vendedor: user.id || '',
                desconto: descontoReaisOrc,
                sub_total: subTotalOrc,
                total: totalOrc,
                userId: user.id,
                observacoes: observacoesOrc
            };
            const response = await gerarOrcamento(requestData);
            if (!response) {
                toast({ title: "Erro ao gerar orcamento", status: "error", duration: 3000, position: isMobile ? "top" : "bottom-right" });
                setLoadingPdf(false);
                return;
            }
            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `orcamento_taga_${new Date().toISOString().replace(/[-:T.]/g, '')}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast({ title: "Orcamento gerado com sucesso!", status: "success", duration: 3000, position: isMobile ? "top" : "bottom-right" });
        } catch {
            toast({ title: "Erro ao gerar orcamento", status: "error", duration: 3000, position: isMobile ? "top" : "bottom-right" });
        }
        setLoadingPdf(false);
    };

    const visualizarHistorico = (item) => {
        setSugestao(item);
        setErroSugestao(null);
        setTabSugestao(0);
    };

    const voltarParaAtual = () => {
        const atual = historico.find(h => h.id === sugestaoAtualId) || historico[0];
        if (atual) setSugestao(atual);
    };

    // Botao do cabecalho do chat:
    // - Desktop: gera diretamente (comportamento classico)
    // - Mobile: abre o sheet; auto-gera apenas se nao houver nada ainda
    const handleBotaoCabecalho = () => {
        if (isMobile) {
            setSheetAberto(true);
            if (!sugestao && !loadingSugestao && !erroSugestao) {
                gerarSugestao();
            }
        } else {
            gerarSugestao();
        }
    };

    const voltarParaLista = () => {
        setContatoSelecionado(null);
        setMensagens([]);
        setSugestao(null);
        setSugestaoAtualId(null);
        setErroSugestao(null);
        setHistorico([]);
        setSheetAberto(false);
    };

    const contatosFiltrados = contatos.filter(c =>
        c.name?.toLowerCase().includes(filtroContato.toLowerCase()) ||
        c.phone?.includes(filtroContato)
    );

    const formatarData = (data) => {
        if (!data) return "";
        const d = new Date(data);
        return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    };

    // Timestamps da POLI vêm em UTC sem indicador de fuso (ex: "2026-04-30 14:23:50").
    // Sem o 'Z', new Date() interpreta como hora local Brasília — 3h adiantado.
    const formatarDataPoli = (data) => {
        if (!data) return "";
        const iso = typeof data === "string" && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(data)
            ? data.replace(" ", "T") + "Z"
            : data;
        const d = new Date(iso);
        return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    };

    const formatarTempo = (s) => {
        const min = Math.floor(s / 60);
        const sec = s % 60;
        return min > 0 ? `${min}m ${sec.toString().padStart(2, '0')}s` : `${sec}s`;
    };

    const getMensagemLoading = () => {
        if (tempoSugestao < 10) return "Lendo historico de mensagens...";
        if (tempoSugestao < 25) return "Analisando imagens enviadas...";
        if (tempoSugestao < 45) return "Buscando produtos no catalogo...";
        if (tempoSugestao < 70) return "Pesquisando codigos equivalentes...";
        if (tempoSugestao < 100) return "Verificando estoque e precos...";
        if (tempoSugestao < 180) return "Montando sugestao de resposta...";
        return "Quase la, finalizando...";
    };

    const visualizandoHistorico = sugestao && sugestaoAtualId && sugestao.id !== sugestaoAtualId;

    // ====== RENDERERS ======

    const renderAbaAtual = () => {
        if (!contatoSelecionado) {
            return (
                <Flex align="center" justify="center" h="100%" p={6}>
                    <Text textAlign="center" color="gray.400" fontSize="sm">
                        Selecione uma conversa e clique em "Gerar Sugestao"
                    </Text>
                </Flex>
            );
        }

        if (loadingSugestao) {
            return (
                <Box p={6}>
                    <VStack spacing={4}>
                        <Spinner size="xl" color="orange.400" thickness="3px" />
                        <Text fontSize="sm" fontWeight="600" color="orange.700" textAlign="center">
                            {getMensagemLoading()}
                        </Text>
                        <Text fontSize="lg" fontWeight="bold" color="orange.500">
                            {formatarTempo(tempoSugestao)}
                        </Text>
                        <Box w="100%">
                            <Progress size="xs" colorScheme="orange" isIndeterminate borderRadius="full" />
                        </Box>
                        <Box bg="orange.50" p={3} borderRadius="md" w="100%">
                            <Text fontSize="xs" color="gray.500" textAlign="center">
                                O assistente esta analisando a conversa, imagens, buscando produtos no catalogo e pesquisando codigos.
                                Isso pode levar ate 5 minutos.
                            </Text>
                        </Box>
                    </VStack>
                </Box>
            );
        }

        if (erroSugestao) {
            const corErro = erroSugestao.status === "error" ? "red" : "yellow";
            const iconColor = corErro === "red" ? "#E53E3E" : "#D69E2E";
            return (
                <Box p={4}>
                    <Flex
                        bg={`${corErro}.50`}
                        borderLeft="4px solid"
                        borderLeftColor={`${corErro}.400`}
                        p={4}
                        borderRadius="md"
                        direction="column"
                        gap={2}
                    >
                        <HStack align="flex-start">
                            <Box pt={0.5}>
                                <AlertTriangle size={18} color={iconColor} />
                            </Box>
                            <Text fontWeight="bold" color={`${corErro}.700`} fontSize="sm">
                                {erroSugestao.titulo}
                            </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.700" lineHeight="1.5">
                            {erroSugestao.descricao}
                        </Text>
                        {erroSugestao.tipo && erroSugestao.tipo !== "unknown" && (
                            <Badge colorScheme="gray" fontSize="9px" alignSelf="flex-start" textTransform="lowercase">
                                {erroSugestao.tipo}
                            </Badge>
                        )}
                        {erroSugestao.podeRetentar && (
                            <Button
                                size="sm"
                                colorScheme="orange"
                                leftIcon={<RefreshCw size={14} />}
                                onClick={gerarSugestao}
                                mt={2}
                                alignSelf="flex-start"
                            >
                                Tentar Novamente
                            </Button>
                        )}
                    </Flex>
                </Box>
            );
        }

        if (sugestao && sugestao.sugestao) {
            return (
                <Box p={4}>
                    <Flex justify="space-between" align="center" mb={3} gap={2}>
                        <VStack align="flex-start" spacing={0} minW={0}>
                            <Text fontSize="xs" color="gray.500" fontWeight="500" isTruncated>
                                {sugestao.gerado_em && formatarData(sugestao.gerado_em)}
                            </Text>
                            {sugestao.id && (
                                <Text fontSize="10px" color="gray.300">
                                    #{sugestao.id}
                                </Text>
                            )}
                        </VStack>
                        <HStack spacing={2} flexShrink={0}>
                            <Tooltip label="Gerar orcamento PDF com os produtos da sugestao">
                                <Button
                                    size="sm"
                                    colorScheme="green"
                                    leftIcon={<FileText size={14} />}
                                    onClick={abrirOrcamento}
                                >
                                    Orcamento
                                </Button>
                            </Tooltip>
                            <Button
                                size="sm"
                                colorScheme="orange"
                                leftIcon={<Copy size={14} />}
                                onClick={copiarSugestao}
                            >
                                Copiar
                            </Button>
                        </HStack>
                    </Flex>
                    <Box
                        bg="gray.50"
                        p={4}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                        userSelect="text"
                        cursor="text"
                    >
                        <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.6">
                            {sugestao.sugestao}
                        </Text>
                    </Box>
                    <Button
                        mt={3}
                        size="sm"
                        variant="outline"
                        colorScheme="orange"
                        leftIcon={<Sparkles size={14} />}
                        onClick={gerarSugestao}
                        isLoading={loadingSugestao}
                        w="100%"
                    >
                        Gerar Novamente
                    </Button>
                </Box>
            );
        }

        // Sem sugestao e sem erro: estado inicial
        return (
            <Flex align="center" justify="center" h="100%" p={6}>
                <VStack spacing={3}>
                    <Sparkles size={32} color="#CBD5E0" />
                    <Text textAlign="center" color="gray.400" fontSize="sm">
                        Nenhuma sugestao gerada ainda
                    </Text>
                    <Button
                        size="sm"
                        colorScheme="orange"
                        leftIcon={<Sparkles size={14} />}
                        onClick={gerarSugestao}
                    >
                        Gerar Sugestao
                    </Button>
                </VStack>
            </Flex>
        );
    };

    const renderAbaHistorico = () => {
        if (!contatoSelecionado) {
            return (
                <Flex align="center" justify="center" h="100%" p={6}>
                    <Text textAlign="center" color="gray.400" fontSize="sm">
                        Selecione uma conversa
                    </Text>
                </Flex>
            );
        }

        if (loadingHistorico) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Spinner color="orange.500" />
                </Flex>
            );
        }

        if (historico.length === 0) {
            return (
                <Flex align="center" justify="center" h="100%" p={6}>
                    <VStack spacing={2}>
                        <History size={32} color="#CBD5E0" />
                        <Text textAlign="center" color="gray.400" fontSize="sm">
                            Nenhuma sugestao gerada ainda
                        </Text>
                        <Text textAlign="center" color="gray.300" fontSize="xs">
                            Cada sugestao que voce gerar ficara salva aqui
                        </Text>
                    </VStack>
                </Flex>
            );
        }

        return (
            <VStack spacing={2} align="stretch" p={3}>
                <Text fontSize="xs" color="gray.400" mb={1}>
                    {historico.length} {historico.length === 1 ? "sugestao" : "sugestoes"} geradas
                </Text>
                {historico.map((item) => {
                    const ativo = sugestao?.id === item.id;
                    const ehAtual = item.id === sugestaoAtualId;
                    const previa = (item.sugestao || "").replace(/\*/g, "").replace(/\n/g, " ").substring(0, 160);
                    return (
                        <Box
                            key={item.id}
                            p={3}
                            border="1px solid"
                            borderColor={ativo ? "orange.300" : "gray.200"}
                            borderRadius="md"
                            cursor="pointer"
                            bg={ativo ? "orange.50" : "white"}
                            _hover={{
                                bg: ativo ? "orange.50" : "gray.50",
                                borderColor: ativo ? "orange.400" : "gray.300"
                            }}
                            onClick={() => visualizarHistorico(item)}
                            transition="all 0.15s"
                        >
                            <Flex justify="space-between" align="center" mb={1.5} gap={2}>
                                <HStack spacing={2} flex={1} minW={0}>
                                    <Clock size={12} color="#718096" style={{ flexShrink: 0 }} />
                                    <Text fontSize="xs" fontWeight="600" color="gray.700" isTruncated>
                                        {formatarData(item.gerado_em)}
                                    </Text>
                                    {ehAtual && (
                                        <Badge colorScheme="green" fontSize="9px" variant="subtle" flexShrink={0}>
                                            atual
                                        </Badge>
                                    )}
                                </HStack>
                                <ChevronRight size={14} color="#A0AEC0" style={{ flexShrink: 0 }} />
                            </Flex>
                            <Text fontSize="xs" color="gray.600" noOfLines={2} lineHeight="1.4">
                                {previa}
                            </Text>
                        </Box>
                    );
                })}
            </VStack>
        );
    };

    const renderSugestaoContent = () => (
        <Flex direction="column" h="100%" minH={0} flex={1}>
            {/* Tabs header */}
            <HStack spacing={0} borderBottom="1px solid" borderColor="gray.100" flexShrink={0}>
                <Button
                    flex={1}
                    size="sm"
                    variant="ghost"
                    borderRadius={0}
                    borderBottom="2px solid"
                    borderBottomColor={tabSugestao === 0 ? "orange.400" : "transparent"}
                    color={tabSugestao === 0 ? "orange.600" : "gray.500"}
                    fontWeight={tabSugestao === 0 ? "600" : "500"}
                    onClick={() => setTabSugestao(0)}
                    _hover={{ bg: "orange.50" }}
                    leftIcon={<Sparkles size={14} />}
                    h="40px"
                >
                    Atual
                </Button>
                <Button
                    flex={1}
                    size="sm"
                    variant="ghost"
                    borderRadius={0}
                    borderBottom="2px solid"
                    borderBottomColor={tabSugestao === 1 ? "orange.400" : "transparent"}
                    color={tabSugestao === 1 ? "orange.600" : "gray.500"}
                    fontWeight={tabSugestao === 1 ? "600" : "500"}
                    onClick={() => setTabSugestao(1)}
                    _hover={{ bg: "orange.50" }}
                    leftIcon={<History size={14} />}
                    h="40px"
                >
                    Historico
                    {historico.length > 0 && (
                        <Badge
                            ml={2}
                            colorScheme={tabSugestao === 1 ? "orange" : "gray"}
                            borderRadius="full"
                            fontSize="10px"
                        >
                            {historico.length}
                        </Badge>
                    )}
                </Button>
            </HStack>

            {/* Banner "visualizando historico" */}
            {visualizandoHistorico && tabSugestao === 0 && (
                <Flex
                    bg="blue.50"
                    borderLeft="3px solid"
                    borderLeftColor="blue.400"
                    px={3}
                    py={2}
                    align="center"
                    gap={2}
                    flexShrink={0}
                >
                    <Box flexShrink={0}>
                        <Info size={14} color="#3182CE" />
                    </Box>
                    <Text color="blue.700" flex={1} fontSize="xs" fontWeight="500">
                        Visualizando historico
                    </Text>
                    <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={voltarParaAtual}
                        flexShrink={0}
                    >
                        Voltar para atual
                    </Button>
                </Flex>
            )}

            {/* Body */}
            <Box flex={1} overflowY="auto" minH={0}>
                {tabSugestao === 0 ? renderAbaAtual() : renderAbaHistorico()}
            </Box>
        </Flex>
    );

    return (
        <Box p={{ base: 2, md: 4 }} maxW="1800px" mx="auto">
            <Flex align="center" mb={3} gap={{ base: 2, md: 3 }}>
                <Sparkles size={22} color="#b35e20" />
                <Text fontSize={{ base: "md", md: "xl" }} fontWeight="bold">
                    Assistente de Vendas IA
                </Text>
                <Badge colorScheme="orange" fontSize="xs">BETA</Badge>
            </Flex>

            <Flex gap={{ base: 2, md: 3 }} h={ALTURA_TOTAL}>

                {/* ========== COL 1 - LISTA DE CONTATOS ========== */}
                <Flex
                    direction="column"
                    w={{ base: "100%", md: "300px" }}
                    minW={{ base: "auto", md: "300px" }}
                    maxW={{ base: "100%", md: "300px" }}
                    bg="white"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    display={{ base: contatoSelecionado ? "none" : "flex", md: "flex" }}
                    overflow="hidden"
                >
                    <Box p={3} borderBottom="1px solid" borderColor="gray.100" flexShrink={0}>
                        <Flex justify="space-between" align="center" mb={2}>
                            <Text fontWeight="bold" fontSize="sm">
                                Vendas ({totalContatos})
                            </Text>
                            <Tooltip label="Atualizar">
                                <IconButton
                                    icon={<RefreshCw size={14} />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => carregarContatos()}
                                    isLoading={loadingContatos}
                                    aria-label="Atualizar"
                                />
                            </Tooltip>
                        </Flex>
                        <HStack mb={2} spacing={1}>
                            {[
                                { label: "Todos", value: null },
                                { label: "Abertos", value: 1 },
                                { label: "Fechados", value: 0 },
                            ].map((f) => (
                                <Button
                                    key={f.label}
                                    size="xs"
                                    variant={filtroStatus === f.value ? "solid" : "outline"}
                                    colorScheme={filtroStatus === f.value ? "orange" : "gray"}
                                    onClick={() => { setFiltroStatus(f.value); carregarContatos(f.value, filtroDias); }}
                                    flex={1}
                                >
                                    {f.label}
                                </Button>
                            ))}
                        </HStack>
                        <HStack mb={2} spacing={1}>
                            {[2, 7, 15, 30].map((d) => (
                                <Tooltip key={d} label={`Ultimos ${d} dias`}>
                                    <Button
                                        size="xs"
                                        variant={filtroDias === d ? "solid" : "outline"}
                                        colorScheme={filtroDias === d ? "orange" : "gray"}
                                        onClick={() => { setFiltroDias(d); carregarContatos(filtroStatus, d); }}
                                        flex={1}
                                    >
                                        {d}d
                                    </Button>
                                </Tooltip>
                            ))}
                        </HStack>
                        <InputGroup size="sm">
                            <InputLeftElement><Search size={14} color="gray" /></InputLeftElement>
                            <Input
                                placeholder="Buscar..."
                                value={filtroContato}
                                onChange={(e) => setFiltroContato(e.target.value)}
                            />
                        </InputGroup>
                    </Box>

                    <Box flex={1} overflowY="auto">
                        {loadingContatos ? (
                            <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
                        ) : contatosFiltrados.length === 0 ? (
                            <Text textAlign="center" py={10} color="gray.400" fontSize="sm">
                                Nenhuma conversa
                            </Text>
                        ) : (
                            <VStack spacing={0} align="stretch">
                                {contatosFiltrados.map((contato) => (
                                    <Box
                                        key={contato.id}
                                        px={3}
                                        py={2}
                                        cursor="pointer"
                                        bg={contatoSelecionado?.id === contato.id ? "orange.50" : "white"}
                                        borderLeft="3px solid"
                                        borderLeftColor={contatoSelecionado?.id === contato.id ? "orange.400" : "transparent"}
                                        _hover={{ bg: "gray.50" }}
                                        onClick={() => selecionarContato(contato)}
                                        borderBottom="1px solid"
                                        borderBottomColor="gray.50"
                                    >
                                        <Flex align="center" gap={2}>
                                            <Avatar size="xs" name={contato.name} bg="orange.400" color="white" />
                                            <Box flex={1} minW={0}>
                                                <Text fontWeight="600" fontSize="xs" isTruncated>{contato.name}</Text>
                                                <Text fontSize="10px" color="gray.400">{contato.phone}</Text>
                                            </Box>
                                            <VStack spacing={0} align="end">
                                                <Text fontSize="10px" color="gray.400">
                                                    {formatarDataPoli(contato.date_last_message)}
                                                </Text>
                                                <Badge
                                                    colorScheme={contato.status === 1 ? "green" : "gray"}
                                                    fontSize="8px"
                                                    variant="subtle"
                                                >
                                                    {contato.status === 1 ? "aberto" : "fechado"}
                                                </Badge>
                                            </VStack>
                                        </Flex>
                                    </Box>
                                ))}
                            </VStack>
                        )}
                    </Box>
                </Flex>

                {/* ========== COL 2 - CONVERSA (CENTRO) ========== */}
                <Flex
                    flex={1}
                    direction="column"
                    bg="white"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    display={{ base: !contatoSelecionado ? "none" : "flex", md: "flex" }}
                    overflow="hidden"
                    minW={0}
                >
                    {!contatoSelecionado ? (
                        <Flex flex={1} align="center" justify="center" direction="column" gap={3} color="gray.300">
                            <MessageSquare size={48} />
                            <Text>Selecione uma conversa</Text>
                        </Flex>
                    ) : (
                        <>
                            {/* Header conversa */}
                            <Flex
                                p={3}
                                borderBottom="1px solid"
                                borderColor="gray.200"
                                align="center"
                                gap={{ base: 2, md: 3 }}
                                bg="white"
                                flexShrink={0}
                            >
                                <IconButton
                                    icon={<ArrowLeft size={18} />}
                                    size="sm"
                                    variant="ghost"
                                    display={{ base: "flex", md: "none" }}
                                    onClick={voltarParaLista}
                                    aria-label="Voltar"
                                />
                                <Avatar size="sm" name={contatoSelecionado.name} bg="orange.400" color="white" />
                                <Box flex={1} minW={0}>
                                    <Text fontWeight="bold" fontSize="sm" isTruncated>{contatoSelecionado.name}</Text>
                                    <Text fontSize="xs" color="gray.500" isTruncated>{contatoSelecionado.phone}</Text>
                                </Box>
                                <Box position="relative" flexShrink={0}>
                                    <Button
                                        size="sm"
                                        colorScheme="orange"
                                        leftIcon={<Sparkles size={14} />}
                                        onClick={handleBotaoCabecalho}
                                        isLoading={loadingSugestao && !isMobile}
                                        loadingText="Gerando..."
                                    >
                                        {isMobile ? "Sugestao" : "Gerar Sugestao"}
                                    </Button>
                                    {isMobile && !loadingSugestao && sugestao && (
                                        <Box
                                            position="absolute"
                                            top="-3px"
                                            right="-3px"
                                            w="10px"
                                            h="10px"
                                            bg="green.400"
                                            borderRadius="full"
                                            border="2px solid white"
                                            pointerEvents="none"
                                        />
                                    )}
                                    {isMobile && loadingSugestao && (
                                        <Box
                                            position="absolute"
                                            top="-3px"
                                            right="-3px"
                                            w="10px"
                                            h="10px"
                                            bg="orange.400"
                                            borderRadius="full"
                                            border="2px solid white"
                                            pointerEvents="none"
                                        />
                                    )}
                                </Box>
                            </Flex>

                            {/* Mensagens */}
                            <Box
                                ref={mensagensRef}
                                flex={1}
                                overflowY="auto"
                                px={{ base: 2, md: 4 }}
                                py={3}
                                bg="#e5ddd5"
                                style={{ minHeight: 0 }}
                            >
                                {loadingMensagens ? (
                                    <Flex justify="center" align="center" h="100%">
                                        <VStack>
                                            <Spinner color="orange.500" size="lg" />
                                            <Text fontSize="sm" color="gray.600">Carregando mensagens...</Text>
                                        </VStack>
                                    </Flex>
                                ) : mensagens.length === 0 ? (
                                    <Flex justify="center" align="center" h="100%">
                                        <Text color="gray.500" fontSize="sm">Nenhuma mensagem</Text>
                                    </Flex>
                                ) : (
                                    <VStack spacing={1} align="stretch">
                                        <Text
                                            textAlign="center"
                                            fontSize="10px"
                                            color="gray.500"
                                            bg="whiteAlpha.700"
                                            borderRadius="md"
                                            px={2}
                                            py={0.5}
                                            alignSelf="center"
                                        >
                                            {mensagens.length} mensagens
                                        </Text>
                                        {mensagens.map((msg) => (
                                            <Flex
                                                key={msg.id}
                                                justify={msg.message_dir === "o" ? "flex-end" : "flex-start"}
                                            >
                                                <Box
                                                    maxW={{ base: "85%", md: "80%" }}
                                                    bg={msg.message_dir === "o" ? "#dcf8c6" : "white"}
                                                    borderRadius="lg"
                                                    px={2.5}
                                                    py={1.5}
                                                    boxShadow="0 1px 0.5px rgba(0,0,0,0.13)"
                                                >
                                                    {msg.message_type === "image" && msg.message_body_url && (
                                                        <img
                                                            src={msg.message_body_url}
                                                            alt="imagem"
                                                            style={{ maxWidth: "220px", borderRadius: "6px", marginBottom: "2px" }}
                                                        />
                                                    )}
                                                    {(msg.message_body_caption || msg.message_body) && (
                                                        <Text fontSize="13px" whiteSpace="pre-wrap">
                                                            {msg.message_body_caption || msg.message_body}
                                                        </Text>
                                                    )}
                                                    <Text fontSize="10px" color="gray.400" textAlign="right">
                                                        {formatarDataPoli(msg.created_at)}
                                                    </Text>
                                                </Box>
                                            </Flex>
                                        ))}
                                    </VStack>
                                )}
                            </Box>

                            {/* Botao scroll */}
                            <Flex justify="center" mt={-10} position="relative" zIndex={2} pointerEvents="none">
                                <IconButton
                                    icon={<ChevronDown size={18} />}
                                    size="sm"
                                    borderRadius="full"
                                    bg="white"
                                    boxShadow="md"
                                    onClick={scrollParaFinal}
                                    pointerEvents="auto"
                                    opacity={0.85}
                                    _hover={{ opacity: 1 }}
                                    aria-label="Ir para o fim"
                                />
                            </Flex>
                        </>
                    )}
                </Flex>

                {/* ========== COL 3 - SUGESTAO IA (DIREITA, APENAS DESKTOP LG+) ========== */}
                <Flex
                    direction="column"
                    w="380px"
                    minW="380px"
                    bg="white"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                    display={{ base: "none", lg: "flex" }}
                >
                    <Flex p={3} borderBottom="1px solid" borderColor="gray.100" align="center" gap={2} flexShrink={0}>
                        <Sparkles size={16} color="#b35e20" />
                        <Text fontWeight="bold" fontSize="sm" color="orange.700">Sugestao IA</Text>
                        {contatoSelecionado && (
                            <Text fontSize="xs" color="gray.400" ml="auto" isTruncated maxW="180px">
                                {contatoSelecionado.name}
                            </Text>
                        )}
                    </Flex>
                    {renderSugestaoContent()}
                </Flex>
            </Flex>

            {/* ========== DRAWER MOBILE (SUGESTAO) ========== */}
            <Drawer
                isOpen={sheetAberto && isMobile}
                placement="bottom"
                onClose={() => setSheetAberto(false)}
                autoFocus={false}
                returnFocusOnClose={false}
            >
                <DrawerOverlay />
                <DrawerContent
                    borderTopRadius="2xl"
                    maxH="85vh"
                    minH="55vh"
                >
                    <Box
                        w="40px"
                        h="4px"
                        bg="gray.300"
                        borderRadius="full"
                        mx="auto"
                        mt={2}
                        mb={0}
                    />
                    <DrawerCloseButton top={3} />
                    <DrawerHeader pb={2} pt={3} pr={10}>
                        <HStack gap={2}>
                            <Sparkles size={18} color="#b35e20" />
                            <Text fontSize="md" color="orange.700">Sugestao IA</Text>
                            {contatoSelecionado && (
                                <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    fontWeight="400"
                                    ml={1}
                                    isTruncated
                                    flex={1}
                                >
                                    {contatoSelecionado.name}
                                </Text>
                            )}
                        </HStack>
                    </DrawerHeader>
                    <DrawerBody p={0} display="flex" flexDirection="column" overflow="hidden">
                        {renderSugestaoContent()}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* ========== MODAL ORCAMENTO ========== */}
            <Modal isOpen={modalOrcamento} onClose={() => setModalOrcamento(false)} size={{ base: "full", md: "4xl" }}>
                <ModalOverlay />
                <ModalContent maxH={{ base: "100vh", md: "90vh" }}>
                    <ModalHeader fontSize="md" pb={2}>
                        <HStack>
                            <FileText size={18} color="#38A169" />
                            <Text>Gerar Orcamento</Text>
                            {nomeCliente && (
                                <Badge colorScheme="green" fontSize="xs" fontWeight="500">{nomeCliente}</Badge>
                            )}
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody overflowY="auto" pb={4}>
                        {/* Dados do cliente */}
                        <Text fontWeight="bold" fontSize="sm" mb={2} color="gray.700">Dados do Cliente</Text>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={5}>
                            <FormControl gridColumn={{ md: "span 2" }}>
                                <FormLabel fontSize="xs" mb={1}>Nome / Razao Social</FormLabel>
                                <Input size="sm" value={nomeCliente} onChange={e => setNomeCliente(e.target.value)} placeholder="Nome do cliente" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Documento (CPF/CNPJ)</FormLabel>
                                <Input size="sm" value={documentoCliente} onChange={e => setDocumentoCliente(e.target.value)} placeholder="00.000.000/0000-00" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Telefone</FormLabel>
                                <Input size="sm" value={telefoneCliente} onChange={e => {
                                    let v = e.target.value.replace(/\D/g, '');
                                    v = v.replace(/^(\d{2})(\d)/g, '($1) $2');
                                    v = v.replace(/(\d{5})(\d)/, '$1-$2');
                                    setTelefoneCliente(v.slice(0, 15));
                                }} placeholder="(00) 00000-0000" />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" mb={1}>Email</FormLabel>
                                <Input size="sm" type="email" value={emailCliente} onChange={e => setEmailCliente(e.target.value)} placeholder="cliente@email.com" />
                            </FormControl>
                            <FormControl gridColumn={{ md: "span 2" }}>
                                <FormLabel fontSize="xs" mb={1}>Endereco</FormLabel>
                                <Input size="sm" value={enderecoCliente} onChange={e => setEnderecoCliente(e.target.value)} placeholder="Endereco completo" />
                            </FormControl>
                        </SimpleGrid>

                        {/* Busca de produtos */}
                        <Text fontWeight="bold" fontSize="sm" mb={2} color="gray.700">Produtos</Text>
                        <HStack mb={2}>
                            <InputGroup size="sm" flex={1}>
                                <InputLeftElement><Search size={14} color="gray" /></InputLeftElement>
                                <Input
                                    placeholder="Buscar produto por nome ou codigo..."
                                    value={buscaManual}
                                    onChange={e => { setBuscaManual(e.target.value); if (!e.target.value) setResultadosBusca([]); }}
                                    onKeyDown={e => e.key === 'Enter' && buscarProdutoManual()}
                                />
                            </InputGroup>
                            <Button size="sm" onClick={buscarProdutoManual} isLoading={loadingBusca} leftIcon={<Search size={14} />} colorScheme="gray">
                                Buscar
                            </Button>
                        </HStack>

                        {/* Resultados da busca */}
                        {resultadosBusca.length > 0 && (
                            <Box border="1px solid" borderColor="gray.200" borderRadius="md" mb={3} maxH="180px" overflowY="auto" bg="white">
                                {resultadosBusca.map(p => {
                                    const jaAdicionado = produtosOrcamento.some(pp => pp.produto_id === p.produto_id);
                                    return (
                                        <Flex
                                            key={p.produto_id}
                                            px={3} py={2}
                                            cursor={jaAdicionado ? "default" : "pointer"}
                                            _hover={{ bg: jaAdicionado ? "white" : "green.50" }}
                                            borderBottom="1px solid"
                                            borderColor="gray.50"
                                            align="center"
                                            gap={2}
                                            opacity={jaAdicionado ? 0.5 : 1}
                                            onClick={() => !jaAdicionado && adicionarProduto(p)}
                                        >
                                            <Box flex={1} minW={0}>
                                                <Text fontSize="xs" fontWeight="600" isTruncated>{p.nome}</Text>
                                                <HStack spacing={3}>
                                                    <Text fontSize="10px" color="gray.500">{p.codigo_global}</Text>
                                                    <Text fontSize="10px" color="gray.500">{p.fabricante_nome}</Text>
                                                    <Text fontSize="10px" color="green.600" fontWeight="600">R$ {formatarPreco(p.valor_venda)}</Text>
                                                    <Text fontSize="10px" color={p.quantidade > 0 ? "blue.500" : "red.500"}>
                                                        Estoque: {p.quantidade}
                                                    </Text>
                                                </HStack>
                                            </Box>
                                            {jaAdicionado ? (
                                                <Badge colorScheme="gray" fontSize="9px">Adicionado</Badge>
                                            ) : (
                                                <IconButton size="xs" icon={<Plus size={12} />} colorScheme="green" variant="ghost" aria-label="Adicionar" />
                                            )}
                                        </Flex>
                                    );
                                })}
                            </Box>
                        )}

                        {/* Loading produtos da sugestao */}
                        {loadingProdutos && (
                            <Flex justify="center" py={4}>
                                <VStack spacing={2}>
                                    <Spinner size="sm" color="green.500" />
                                    <Text fontSize="xs" color="gray.500">Buscando produtos da sugestao...</Text>
                                </VStack>
                            </Flex>
                        )}

                        {/* Tabela de produtos */}
                        {produtosOrcamento.length > 0 ? (
                            <Box overflowX="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                                <Table size="sm" variant="simple">
                                    <Thead>
                                        <Tr bg="gray.100">
                                            <Th fontSize="10px" textAlign="center">Produto</Th>
                                            <Th fontSize="10px" textAlign="center">Codigo</Th>
                                            <Th fontSize="10px" textAlign="center">Marca</Th>
                                            <Th fontSize="10px" textAlign="center">Preco Unt.</Th>
                                            <Th fontSize="10px" textAlign="center" w="70px">Qtd</Th>
                                            <Th fontSize="10px" textAlign="center">Total</Th>
                                            <Th w="40px"></Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {produtosOrcamento.map(p => (
                                            <Tr key={p.produto_id}>
                                                <Td fontSize="xs" maxW="200px" py={2}>
                                                    <Text isTruncated title={p.nome}>{p.nome}</Text>
                                                </Td>
                                                <Td fontSize="xs" textAlign="center" py={2}>{p.codigo_global}</Td>
                                                <Td fontSize="xs" textAlign="center" py={2}>{p.fabricante_nome || '-'}</Td>
                                                <Td fontSize="xs" textAlign="center" whiteSpace="nowrap" py={2}>R$ {formatarPreco(p.valor_venda)}</Td>
                                                <Td textAlign="center" py={2}>
                                                    <Input
                                                        size="xs"
                                                        type="number"
                                                        min={1}
                                                        max={p.quantidade || 999}
                                                        value={p.quantidade_orcamento}
                                                        onChange={e => handleQtdChange(p.produto_id, e.target.value)}
                                                        w="55px"
                                                        textAlign="center"
                                                        bg="white"
                                                    />
                                                </Td>
                                                <Td fontSize="xs" textAlign="center" whiteSpace="nowrap" fontWeight="600" py={2}>
                                                    R$ {formatarPreco((p.valor_venda * (p.quantidade_orcamento || 1)).toFixed(2))}
                                                </Td>
                                                <Td textAlign="center" py={2}>
                                                    <IconButton
                                                        size="xs"
                                                        icon={<Trash2 size={12} />}
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        onClick={() => removerProdutoOrc(p.produto_id)}
                                                        aria-label="Remover"
                                                    />
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        ) : !loadingProdutos && (
                            <Flex justify="center" py={6} direction="column" align="center" gap={2}>
                                <Package size={28} color="#CBD5E0" />
                                <Text fontSize="xs" color="gray.400">Nenhum produto encontrado na sugestao</Text>
                                <Text fontSize="10px" color="gray.300">
                                    Use a busca acima para adicionar produtos manualmente
                                </Text>
                            </Flex>
                        )}

                        {/* Resumo financeiro */}
                        {produtosOrcamento.length > 0 && (
                            <Box mt={4}>
                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                                    <FormControl>
                                        <FormLabel fontSize="xs" mb={1}>Valor do Frete</FormLabel>
                                        <InputGroup size="sm">
                                            <InputLeftAddon fontSize="xs">R$</InputLeftAddon>
                                            <Input value={valorFrete} onChange={handleFreteChange} placeholder="0,00" textAlign="right" />
                                        </InputGroup>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="xs" mb={1}>Desconto (%)</FormLabel>
                                        <Input
                                            size="sm"
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={descontoOrc}
                                            onChange={e => setDescontoOrc(Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
                                            placeholder="0"
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="xs" mb={1}>Observacoes</FormLabel>
                                        <Textarea size="sm" rows={1} value={observacoesOrc} onChange={e => setObservacoesOrc(e.target.value)} placeholder="Observacoes adicionais..." />
                                    </FormControl>
                                </SimpleGrid>

                                <Box bg="gray.50" p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
                                    <HStack justify="space-between" mb={1}>
                                        <Text fontSize="xs" color="gray.600">Subtotal</Text>
                                        <Text fontSize="xs" fontWeight="600">R$ {formatarPreco(subTotalOrc)}</Text>
                                    </HStack>
                                    {descontoOrc > 0 && (
                                        <HStack justify="space-between" mb={1}>
                                            <Text fontSize="xs" color="red.500">Desconto ({descontoOrc}%)</Text>
                                            <Text fontSize="xs" color="red.500">- R$ {formatarPreco(descontoReaisOrc)}</Text>
                                        </HStack>
                                    )}
                                    {parseFormattedCurrency(valorFrete) > 0 && (
                                        <HStack justify="space-between" mb={1}>
                                            <Text fontSize="xs" color="gray.600">Frete</Text>
                                            <Text fontSize="xs">R$ {valorFrete}</Text>
                                        </HStack>
                                    )}
                                    <Box borderTop="1px solid" borderColor="gray.300" pt={2} mt={1}>
                                        <HStack justify="space-between">
                                            <Text fontSize="sm" fontWeight="bold">Total</Text>
                                            <Text fontSize="md" fontWeight="bold" color="green.600">R$ {formatarPreco(totalOrc)}</Text>
                                        </HStack>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="gray.100">
                        <Button variant="ghost" mr={3} onClick={() => setModalOrcamento(false)} size="sm">
                            Cancelar
                        </Button>
                        <Button
                            colorScheme="green"
                            leftIcon={<FileText size={14} />}
                            onClick={handleGerarPdf}
                            isLoading={loadingPdf}
                            loadingText="Gerando PDF..."
                            isDisabled={produtosOrcamento.length === 0}
                            size="sm"
                        >
                            Gerar Orcamento PDF
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default AssistenteVendas;
